import { CouponArt } from "@/components/marketing/coupon/CouponArt"
import { CouponTicket } from "@/components/marketing/coupon/CouponTicket"
import { CouponFreeItemAction } from "@/components/marketing/coupon/CouponFreeItemAction"
import { CouponTicketTitle } from "@/components/marketing/coupon/CouponTicketTitle"
import { Button, SectionHeader, Text } from "@/components/ui"
import { useCouponTerms } from "@/hooks/use-coupon-terms"
import { couponBenefitHeadline } from "@/lib/coupon-benefit"
import { getStoredCompany } from "@/lib/company-storage"
import { couponCodeKey } from "@/lib/pending-coupon"

type WalletCouponListProps = {
  wallet: readonly string[]
  applied: readonly string[]
  onPick: (code: string) => void
}

/** "Seus cupons": the customer's other coupons at this store as small tickets, each one tap away from the order (it takes the place of the current one). */
export function WalletCouponList({ wallet, applied, onPick }: WalletCouponListProps) {
  const appliedKeys = new Set(applied.map(couponCodeKey))
  const others = wallet.filter((code) => !appliedKeys.has(couponCodeKey(code)))
  const terms = useCouponTerms(getStoredCompany()?.id ?? null, others)
  if (others.length === 0) return null

  return (
    <div className="mt-4">
      <SectionHeader title="Seus cupons" description={others.length === 1 ? "1 cupom guardado" : `${others.length} cupons guardados`} />
      <ul className="flex flex-col gap-2">
        {others.map((code) => {
          const read = terms[code]
          const words = read ? couponBenefitHeadline(read.benefit, read.reward, read.maxDiscountCents, read.offer) : null
          const headline = words?.headline ?? null
          const itemChoice = read?.offer?.kind === "free-items" && read.offer.refs.length > 1 ? (words?.detail ?? null) : null
          return (
            <CouponTicket key={code} as="li" size="sm" stub={<CouponArt size={26} />}>
              <div className="flex min-w-0 items-center gap-2 py-2 pl-3">
                <div className="min-w-0 flex-1">
                  <CouponTicketTitle headline={headline ?? "Cupom"} code={code} />
                  {itemChoice ? (
                    <Text as="p" size="xs" tone="muted" truncate>
                      {itemChoice}
                    </Text>
                  ) : null}
                  <CouponFreeItemAction terms={read} tone="store" onDone={(added) => (added ? onPick(code) : undefined)} />
                </div>
                <Button variant="ghost" tone="neutral" size="sm" weight="semibold" aria-label={`Usar o cupom ${code}`} onClick={() => onPick(code)} className="shrink-0">
                  <Text as="span" size="sm" tone="primary">
                    Usar
                  </Text>
                </Button>
              </div>
            </CouponTicket>
          )
        })}
      </ul>
    </div>
  )
}
